const SUPPORTED_TYPES = new Set([
  "session_flagged",
  "user_added_to_org",
  "user_linked_to_org",
  "role_change",
  "onboarding_email_sent",
  "linked_email_sent",
  "linked_email_skipped",
  "password_reset_requested",
  "first_login_welcome",
  "flag_case_open",
  "flag_case_claimed",
  "flag_case_resolved",
  "flag_case_coach_message_sent",
  "flag_case_reminder",
  "flag_case_escalated",
]);

const COACH_WORKFLOW_TYPES = new Set([
  "session_flagged",
  "flag_case_open",
  "flag_case_claimed",
  "flag_case_resolved",
  "flag_case_coach_message_sent",
  "flag_case_reminder",
  "flag_case_escalated",
]);

const PERSONAL_TYPES = new Set([
  "first_login_welcome",
  "password_reset_requested",
  "role_change",
  "onboarding_email_sent",
  "linked_email_sent",
  "linked_email_skipped",
  "user_added_to_org",
  "user_linked_to_org",
]);

function roleOf(user) {
  return String(user?.role || "").trim().toLowerCase();
}

function toMillis(value) {
  if (!value) return Date.now();
  if (typeof value === "number") return value;
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? Date.now() : parsed;
}

function isOwnNotification(row, user) {
  const ownEmail = String(user?.email || "").trim().toLowerCase();
  const targetEmail = String(row?.targetEmail || "").trim().toLowerCase();
  return row?.targetUserId === user?.uid || row?.actorId === user?.uid || (targetEmail && targetEmail === ownEmail);
}

function shortId(value) {
  const raw = String(value || "").trim();
  if (!raw) return "unknown";
  return raw.slice(-4);
}

function safeCaseLabel(row) {
  const rawCaseId = String(row?.flagCaseId || row?.caseId || row?.id || "").trim();
  if (rawCaseId) return `Flag case ${shortId(rawCaseId)}`;
  const rawSessionId = String(row?.sessionId || row?.session_id || "").trim();
  if (rawSessionId) return `Session ${shortId(rawSessionId)}`;
  return "Coach review case";
}

function memberLabel(row, user) {
  const role = roleOf(user);
  if (COACH_WORKFLOW_TYPES.has(String(row?.type || ""))) return safeCaseLabel(row);
  if (role === "superadmin" || role === "coach") {
    if (row?.targetUserId) return `Member #${shortId(row.targetUserId)}`;
    return row?.user && !String(row.user).includes("@") ? row.user : "Member";
  }
  if (role === "admin") {
    if (row?.targetUserId) return `Member #${shortId(row.targetUserId)}`;
    if (row?.sessionId) return `Member session #${shortId(row.sessionId)}`;
    return "Member";
  }
  return "your account";
}

function actorLabel(row, user) {
  const role = roleOf(user);
  if (COACH_WORKFLOW_TYPES.has(String(row?.type || ""))) return "Coach review";
  if (role === "superadmin" || role === "coach") return row?.actorEmail || "System";
  if (role === "admin") return row?.actorId ? `User #${shortId(row.actorId)}` : "System";
  return "System";
}

export function filterVisibleNotifications(rows, user, readNotifIds = new Set()) {
  if (!user?.uid) return [];
  const role = roleOf(user);
  if (role === "investor") return [];
  const source = Array.isArray(rows) ? rows : [];

  return source.filter((row) => {
    const type = String(row?.type || "");
    if (!SUPPORTED_TYPES.has(type)) return false;
    if (row?.id && readNotifIds.has(String(row.id))) return false;
    const own = isOwnNotification(row, user);

    if (role === "member") {
      if (COACH_WORKFLOW_TYPES.has(type)) return false;
      if (!PERSONAL_TYPES.has(type)) return false;
      return own;
    }

    if (role === "coach") {
      if (COACH_WORKFLOW_TYPES.has(type)) return true;
      if (PERSONAL_TYPES.has(type)) return own;
      return false;
    }

    if (role === "admin") {
      return true;
    }

    if (role === "superadmin") {
      return true;
    }

    return own;
  });
}

export function getNotificationTitle(row, user) {
  const type = String(row?.type || "");
  const role = roleOf(user);
  const label = memberLabel(row, user);

  if (type === "first_login_welcome") {
    return "Welcome to Nia COACH. Start by opening a chat and saying: Hello Nia, this is my first session.";
  }

  if (role === "member") {
    if (type === "user_added_to_org" || type === "user_linked_to_org") return "You were added to an organisation";
    if (type === "role_change") return `Your role was updated${row?.role ? ` • ${row.role}` : ""}`;
    if (type === "onboarding_email_sent") return "Welcome email sent to your account";
    if (type === "linked_email_sent") return "Organisation access email sent";
    if (type === "linked_email_skipped") return "Organisation access updated";
    if (type === "password_reset_requested") return "Password reset requested";
  }

  if (type === "session_flagged") {
    const sourceLabel = String(row?.source || "").trim().toLowerCase() === "coach" ? "Coach" : "Nia";
    return `${sourceLabel} flagged a session • ${safeCaseLabel(row)}`;
  }
  if (type === "user_added_to_org") return `User added to organisation • ${label}`;
  if (type === "user_linked_to_org") return `Existing user linked • ${label}`;
  if (type === "role_change") return `Role changed${row?.role ? ` • ${row.role}` : ""}`;
  if (type === "onboarding_email_sent") return `Welcome email sent • ${label}`;
  if (type === "linked_email_sent") return `Linked-access email sent • ${label}`;
  if (type === "linked_email_skipped") return `Linked-access email not sent • ${label}`;
  if (type === "password_reset_requested") return `Password reset requested • ${label}`;
  if (type === "flag_case_open") return `Flag case opened • ${safeCaseLabel(row)}`;
  if (type === "flag_case_claimed") return `Flag case claimed • ${safeCaseLabel(row)}`;
  if (type === "flag_case_resolved") return `Flag case resolved • ${safeCaseLabel(row)}`;
  if (type === "flag_case_coach_message_sent") return `Coach message sent • ${safeCaseLabel(row)}`;
  if (type === "flag_case_reminder") return `Flag case reminder • ${safeCaseLabel(row)}`;
  if (type === "flag_case_escalated") return `Flag case escalated • ${safeCaseLabel(row)}`;
  return "Notification";
}

export function getNotificationMeta(row, user) {
  const actor = actorLabel(row, user);
  const ts = toMillis(row?.createdAt);
  const when = ts ? new Date(ts).toLocaleString() : "Just now";
  return `${actor} • ${when}`;
}
